import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  THRESHOLD_STEPS,
  SLIDER_LABELS,
  SLIDER_COLORS,
  TOOLTIP_COLORS,
} from "./constants";

interface SliderProps {
  defaultLeftIdx?: number;
  defaultRightIdx?: number;
  onChange?: (leftIdx: number, rightIdx: number) => void;
  showComparison?: boolean;
  originalLeftIdx?: number;
  originalRightIdx?: number;
}

const Slider: React.FC<SliderProps> = ({
  defaultLeftIdx = 2,
  defaultRightIdx = 6,
  onChange,
  showComparison = false,
  originalLeftIdx,
  originalRightIdx,
}) => {
  const [leftIdx, setLeftIdx] = useState(defaultLeftIdx);
  const [rightIdx, setRightIdx] = useState(defaultRightIdx);
  const [activeHandle, setActiveHandle] = useState<"left" | "right" | null>(
    null,
  );
  const [isDragging, setIsDragging] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);
  const dragHandleRef = useRef<"left" | "right" | null>(null);

  const getPos = (index: number): number => (index + 1) * 10;

  const hasChanges =
    showComparison &&
    (leftIdx !== (originalLeftIdx ?? defaultLeftIdx) ||
      rightIdx !== (originalRightIdx ?? defaultRightIdx));

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!dragHandleRef.current || !trackRef.current) return;

      const rect = trackRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const percentage = (clickX / rect.width) * 10;
      const newIdx = Math.max(0, Math.min(9, Math.round(percentage - 1)));

      if (dragHandleRef.current === "left" && newIdx < rightIdx) {
        setLeftIdx(newIdx);
      } else if (
        dragHandleRef.current === "right" &&
        newIdx > leftIdx &&
        newIdx <= 9
      ) {
        setRightIdx(newIdx);
      }
    },
    [leftIdx, rightIdx],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setActiveHandle(null);
    dragHandleRef.current = null;
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const startDragging = (handle: "left" | "right"): void => {
    setIsDragging(true);
    setActiveHandle(handle);
    dragHandleRef.current = handle;
  };

  const handleTrackClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (!trackRef.current || isDragging) return;

    const rect = trackRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = (clickX / rect.width) * 10;
    const clickedIdx = Math.max(0, Math.min(9, Math.round(percentage - 1)));

    const distLeft = Math.abs(clickedIdx - leftIdx);
    const distRight = Math.abs(clickedIdx - rightIdx);

    if (distLeft < distRight) {
      if (clickedIdx < rightIdx) setLeftIdx(clickedIdx);
    } else {
      if (clickedIdx > leftIdx && clickedIdx <= 9) setRightIdx(clickedIdx);
    }
  };

  useEffect(() => {
    if (onChange) {
      onChange(leftIdx, rightIdx);
    }
  }, [leftIdx, rightIdx, onChange]);

  return (
    <div className="w-full px-10 min-w-[650px] select-none font-sans">
      <div className="flex justify-left mb-20">
        <p className="text-[10px] font-bold text-gray-700 uppercase tracking-[0.2em]">
          {SLIDER_LABELS.RISK_STATUS_THRESHOLD}
        </p>
      </div>

      <div
        ref={trackRef}
        onClick={handleTrackClick}
        className="relative h-2 w-full bg-gray-200 rounded-full mb-12 cursor-pointer"
      >
        <div className="absolute left-0 right-0 flex justify-between -top-2 z-0 pointer-events-none">
          {Array.from({ length: 11 }).map((_, i) => (
            <div key={i} className="h-6 w-[1.5px] bg-gray-700" />
          ))}
        </div>

        <div
          className="absolute h-full bg-[#D32F2F] rounded-l-full transition-all duration-200"
          style={{ width: `${getPos(leftIdx)}%` }}
        />
        <div
          className="absolute h-full bg-[#F9A825] transition-all duration-200"
          style={{
            left: `${getPos(leftIdx)}%`,
            width: `${getPos(rightIdx) - getPos(leftIdx)}%`,
          }}
        />
        <div
          className="absolute h-full bg-[#2E7D32] rounded-r-full transition-all duration-200"
          style={{
            left: `${getPos(rightIdx)}%`,
            width: `${100 - getPos(rightIdx)}%`,
          }}
        />

        <div className="absolute left-0 right-0 flex justify-between top-8 pointer-events-none">
          <span className="text-[11px] text-gray-700 -translate-x-1/2 w-0 flex justify-center">
            {SLIDER_LABELS.MIN_VALUE}
          </span>
          {THRESHOLD_STEPS.map((step, i) => (
            <span
              key={i}
              className="text-[11px] text-gray-700 -translate-x-1/2 w-0 flex justify-center"
            >
              {i === 9 ? SLIDER_LABELS.MAX_VALUE : step.low}
            </span>
          ))}
        </div>

        <div
          className="absolute top-1/2 z-30 -translate-y-1/2 -translate-x-1/2 flex items-center justify-center"
          style={{
            left: `${getPos(leftIdx)}%`,
            zIndex: activeHandle === "left" ? 50 : 30,
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            startDragging("left");
          }}
          onMouseEnter={() => !isDragging && setActiveHandle("left")}
          onMouseLeave={() => !isDragging && setActiveHandle(null)}
        >
          <Tooltip
            low={THRESHOLD_STEPS[leftIdx].low}
            high={THRESHOLD_STEPS[leftIdx].high}
            color={TOOLTIP_COLORS.RED}
            isActive={activeHandle === "left"}
          />
          <div
            className={`w-6 h-6 bg-white border-2 rounded-full shadow-md cursor-grab active:cursor-grabbing transition-all duration-150 ${
              activeHandle === "left"
                ? "border-[#D32F2F] scale-125 shadow-lg ring-4 ring-[#D32F2F]/20"
                : "border-gray-300 hover:border-[#D32F2F] hover:scale-110"
            }`}
          />
        </div>

        <div
          className="absolute top-1/2 z-30 -translate-y-1/2 -translate-x-1/2 flex items-center justify-center"
          style={{
            left: `${getPos(rightIdx)}%`,
            zIndex: activeHandle === "right" ? 50 : 30,
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            startDragging("right");
          }}
          onMouseEnter={() => !isDragging && setActiveHandle("right")}
          onMouseLeave={() => !isDragging && setActiveHandle(null)}
        >
          <Tooltip
            low={THRESHOLD_STEPS[rightIdx].low}
            high={THRESHOLD_STEPS[rightIdx].high}
            color={TOOLTIP_COLORS.ORANGE}
            isActive={activeHandle === "right"}
          />
          <div
            className={`w-6 h-6 bg-white border-2 rounded-full shadow-md cursor-grab active:cursor-grabbing transition-all duration-150 ${
              activeHandle === "right"
                ? "border-[#F9A825] scale-125 shadow-lg ring-4 ring-[#F9A825]/20"
                : "border-gray-300 hover:border-[#F9A825] hover:scale-110"
            }`}
          />
        </div>
      </div>

      <div className="mt-20 flex justify-center">
        <div className="border border-gray-200 rounded-xl p-6 bg-white shadow-sm max-w-[700px] w-full">
          {hasChanges && (
            <div className="mb-4 pb-4 border-b border-gray-200 flex items-center justify-between">
              <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide">
                {SLIDER_LABELS.MODIFIED_VALUES}
              </p>
              <p className="text-xs font-medium text-gray-700 uppercase tracking-wide">
                {SLIDER_LABELS.ORIGINAL_VALUES}
              </p>
            </div>
          )}

          <div className="space-y-3 text-[15px]">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2 flex-1">
                <span className="text-gray-700 font-medium w-20">
                  {SLIDER_LABELS.HIGH_LABEL}
                </span>
                <span
                  className="font-bold"
                  style={{ color: SLIDER_COLORS.HIGH_RISK }}
                >
                  {SLIDER_LABELS.MIN_VALUE} - {THRESHOLD_STEPS[leftIdx].low}
                </span>
              </div>
              {hasChanges && (
                <span className="text-sm font-medium text-gray-700 italic">
                  {SLIDER_LABELS.MIN_VALUE} -{" "}
                  {THRESHOLD_STEPS[originalLeftIdx ?? defaultLeftIdx].low}
                </span>
              )}
            </div>

            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2 flex-1">
                <span className="text-gray-700 font-medium w-20">
                  {SLIDER_LABELS.MEDIUM_LABEL}
                </span>
                <span
                  className="font-bold"
                  style={{ color: SLIDER_COLORS.MEDIUM_RISK }}
                >
                  {THRESHOLD_STEPS[leftIdx].high} -{" "}
                  {THRESHOLD_STEPS[rightIdx].low}
                </span>
              </div>
              {hasChanges && (
                <span className="text-sm font-medium text-gray-700 italic">
                  {THRESHOLD_STEPS[originalLeftIdx ?? defaultLeftIdx].high} -{" "}
                  {THRESHOLD_STEPS[originalRightIdx ?? defaultRightIdx].low}
                </span>
              )}
            </div>

            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2 flex-1">
                <span className="text-gray-700 font-medium w-20">
                  {SLIDER_LABELS.LOW_LABEL}
                </span>
                <span
                  className="font-bold"
                  style={{ color: SLIDER_COLORS.LOW_RISK }}
                >
                  {THRESHOLD_STEPS[rightIdx].high} - {SLIDER_LABELS.MAX_VALUE}
                </span>
              </div>
              {hasChanges && (
                <span className="text-sm font-medium text-gray-700 italic">
                  {THRESHOLD_STEPS[originalRightIdx ?? defaultRightIdx].high} -{" "}
                  {SLIDER_LABELS.MAX_VALUE}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Tooltip: React.FC<{
  low: string;
  high: string;
  color: typeof TOOLTIP_COLORS.RED | typeof TOOLTIP_COLORS.ORANGE;
  isActive: boolean;
}> = ({ low, high, color, isActive }) => (
  <div
    className={`absolute -top-14 left-1/2 -translate-x-1/2 bg-white border px-3 py-1.5 rounded-lg shadow-lg text-[13px] font-bold whitespace-nowrap transition-all duration-150 ${
      isActive
        ? "z-[100] border-gray-300 scale-105"
        : "z-[10] border-gray-100 opacity-95"
    }`}
  >
    <span
      style={{
        color:
          color === TOOLTIP_COLORS.RED
            ? SLIDER_COLORS.HIGH_RISK
            : SLIDER_COLORS.MEDIUM_RISK,
      }}
    >
      {low}
    </span>
    <span className="mx-1.5 text-gray-300">/</span>
    <span
      style={{
        color:
          color === TOOLTIP_COLORS.RED
            ? SLIDER_COLORS.MEDIUM_RISK
            : SLIDER_COLORS.LOW_RISK,
      }}
    >
      {high}
    </span>
    <div
      className={`absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-white rotate-45 border-r border-b ${
        isActive ? "border-gray-300" : "border-gray-100"
      }`}
    />
  </div>
);

export default Slider;
